import React, { useState } from 'react'

import {
  AlignItems,
  Box,
  Button,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  Icon,
  JustifyContent,
  Link as CitrusLink,
  LinkVariant,
  ListItem,
  Text,
  withCitrusTheme,
  ZIndex,
  UnorderedList,
} from '@gousto-internal/citrus-react'

type LinkProps = {
  href: string
  text: string
}

type TitleProps = {
  isExpanded: boolean
  titleText: string
  onToggle: () => void
}

export type DropdownHeaderProps = {
  titleText: string
  links: LinkProps[]
}

const StyledTitle = withCitrusTheme(Box, (theme) => ({
  '&:hover': {
    textDecorationLine: 'underline',
    textDecorationColor: theme.color(Color.White),
    textDecorationThickness: '2px',
    textUnderlineOffset: '6px',
  },
}))

const Title = ({ isExpanded, titleText, onToggle }: TitleProps) => (
  <StyledTitle
    data-testid={`${titleText.replace(/\s+/g, '-')}-header`}
    display={Display.Flex}
    alignItems={AlignItems.Center}
  >
    <Button
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onToggle()
        }
      }}
      aria-haspopup="menu"
      aria-expanded={isExpanded}
      style={{
        backgroundColor: 'transparent',
        padding: 0,
        cursor: 'pointer',
      }}
      tabIndex={0}
    >
      <Text color={Color.White} size={1} fontWeight={FontWeight.SemiBold}>
        {titleText.toUpperCase()}
      </Text>
      <Icon name={isExpanded ? 'chevron_up' : 'chevron_down'} style={{ color: Color.White }} />
    </Button>
  </StyledTitle>
)

const StyledLink = withCitrusTheme(CitrusLink, (theme) => ({
  display: 'flex',
  alignItems: 'center',
  height: theme.spacing(8),
  '&:hover, &:focus': {
    backgroundColor: theme.color(Color.Secondary_50),
    borderRadius: '8px',
    padding: '0px 12px',
    width: '100%',
  },
}))

const ListItemLink = ({ href, text }: LinkProps) => (
  <StyledLink
    href={href}
    variant={LinkVariant.None}
    color={Color.ColdGrey_800}
    style={{
      padding: '0px 12px',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
    }}
    data-testid="dropdown-link"
  >
    {text}
  </StyledLink>
)

export const DropdownHeader = ({ titleText, links }: DropdownHeaderProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const handleToggle = () => setIsExpanded((prev) => !prev)
  const handleMouseEnter = () => setIsExpanded(true)
  const handleMouseLeave = () => setIsExpanded(false)

  return (
    <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Box display={Display.InlineBlock} position={DisplayPosition.Relative} paddingRight={5}>
        <Title isExpanded={isExpanded} titleText={titleText} onToggle={handleToggle} />
        {isExpanded && (
          <>
            {/* Transparent Box to fill gap between title and menu items to maintain hover area */}
            <Box position={DisplayPosition.Absolute} top="100%" width="100%" height="60%" />
            <Box
              display={Display.Block}
              flexDirection={FlexDirection.Column}
              alignItems={AlignItems.FlexStart}
              justifyContent={JustifyContent.Center}
              position={DisplayPosition.Absolute}
              top="160%"
              zIndex={ZIndex.Top}
              bg={Color.White}
              paddingV={4}
              paddingH={2}
              role="menu"
              aria-label={`${titleText} menu`}
              style={{ borderRadius: '0 0 8px 8px' }}
            >
              <UnorderedList style={{ margin: 0, padding: 0 }}>
                <ListItem style={{ display: 'block' }}>
                  {links.map((link) => (
                    <ListItemLink key={link.text} href={link.href} text={link.text} />
                  ))}
                </ListItem>
              </UnorderedList>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
